import { useRef, useCallback } from 'react';

export function useAudio() {
  const ctxRef   = useRef<AudioContext | null>(null);
  const nodesRef = useRef<OscillatorNode[]>([]);

  const getCtx = useCallback((): AudioContext => {
    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      ctxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      nodesRef.current = [];
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume().catch(() => null);
    }
    return ctxRef.current;
  }, []);

  const silenceAll = useCallback((cancelSpeech = false) => {
    const now = ctxRef.current?.currentTime ?? 0;
    for (const osc of nodesRef.current) {
      try { osc.stop(now); } catch { /* already stopped */ }
    }
    nodesRef.current = [];
    if (cancelSpeech) {
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    }
  }, []);

  const unsilence = useCallback(() => {
    if (ctxRef.current?.state === 'suspended') {
      ctxRef.current.resume().catch(() => null);
    }
  }, []);

  const makeOsc = useCallback((ctx: AudioContext): OscillatorNode => {
    const osc = ctx.createOscillator();
    nodesRef.current.push(osc);
    osc.onended = () => {
      nodesRef.current = nodesRef.current.filter(n => n !== osc);
    };
    return osc;
  }, []);

  const playBeep = useCallback((frequency = 880, durationMs = 120, gainLevel = 0.35, delayMs = 0) => {
    try {
      const ctx  = getCtx();
      const osc  = makeOsc(ctx);
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency, ctx.currentTime);
      const start = ctx.currentTime + delayMs / 1000;
      const end   = start + durationMs / 1000;
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(gainLevel, start + 0.01);
      gain.gain.setValueAtTime(gainLevel, end - 0.02);
      gain.gain.linearRampToValueAtTime(0, end);
      osc.start(start);
      osc.stop(end + 0.05);
    } catch { /* ignore */ }
  }, [getCtx, makeOsc]);

  const playCountdownBeeps = useCallback((count = 5) => {
    for (let i = 0; i < count; i++) playBeep(880, 130, 0.4, i * 1000);
  }, [playBeep]);

  const playBuzzer = useCallback(() => {
    try {
      const ctx      = getCtx();
      const duration = 0.8;
      [0, 12].forEach(detune => {
        const osc  = makeOsc(ctx);
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(220 + detune, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(110, ctx.currentTime + duration);
        gain.gain.setValueAtTime(0.5, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + duration + 0.05);
      });
    } catch { /* ignore */ }
  }, [getCtx, makeOsc]);

  const playMatchExpiry = useCallback(() => {
    [0, 300, 600].forEach(d => playBeep(440, 300, 0.6, d));
    setTimeout(() => playBuzzer(), 900);
  }, [playBeep, playBuzzer]);

  // ── Half-time announcement — plays the pre-recorded audio file ───────────
  // Uses an HTML Audio element rather than SpeechSynthesis, which is blocked
  // by Chrome's autoplay policy when called from non-gesture contexts.
  const speakAnnouncement = useCallback((_text: string) => {
    try {
      const audio = new Audio('/half-time.aac');
      audio.volume = 1.0;
      audio.play().catch(() => {
        // Autoplay blocked — try once more after a tiny delay
        setTimeout(() => audio.play().catch(() => null), 100);
      });
    } catch { /* ignore */ }
  }, []);

  return { playBeep, playCountdownBeeps, playBuzzer, playMatchExpiry, speakAnnouncement, silenceAll, unsilence };
}
